import { motion } from 'framer-motion';
import { Users, TrendingUp, Euro } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';

interface TopClient {
  id: string;
  name: string;
  totalSales: number;
  salesCount: number;
  lastSaleDate?: string;
}

interface TopClientsProps {
  clients: TopClient[];
}

export function TopClients({ clients }: TopClientsProps) {
  const maxSales = Math.max(...clients.map(c => c.totalSales), 1);

  return (
    <div className="space-y-4">
      {clients.map((client, index) => {
        const percentage = (client.totalSales / maxSales) * 100;
        
        return (
          <motion.div
            key={client.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ x: 4 }}
            className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          >
            {/* Rank */}
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
              {index + 1}
            </div>

            {/* Client info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {client.name}
                </p>
                <div className="flex items-center text-emerald-600 dark:text-emerald-400">
                  <Euro className="w-3 h-3 mr-1" />
                  <span className="font-bold">{formatCurrency(client.totalSales)}</span>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="h-2 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 1, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
                />
              </div>

              {/* Stats */}
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center">
                    <Users className="w-3 h-3 mr-1" />
                    <span>{client.salesCount} ventas</span>
                  </div>
                  {client.lastSaleDate && (
                    <div className="flex items-center">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      <span>{formatDate(client.lastSaleDate)}</span>
                    </div>
                  )}
                </div>
                <div className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                  {percentage.toFixed(0)}%
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}

      {clients.length === 0 && (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <Users className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No hay datos de clientes
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Los clientes top aparecerán aquí
          </p>
        </div>
      )}
    </div>
  );
}